import React from "react";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import is_null from "../../helpers/is_null";
import LetterHeader from "../../components/LetterHeader";
import useAuthStore from "../../stores/useAuthStore";

import styled from "styled-components";

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media print {
    padding: 0;
    max-width: 100%;
  }
`;

const StudentInfoCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media print {
    box-shadow: none;
    border: 1px solid #000;
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Value = styled.div`
  color: #111827;
  font-size: 1.125rem;
  font-weight: 600;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  @media print {
    box-shadow: none;
    border: 1px solid #000;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;
  font-family: sans-serif;
  min-width: 400px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);

  thead tr {
    background-color: #009879;
    color: #ffffff;
    text-align: left;
  }

  th,
  td {
    padding: 12px 15px;
  }

  tbody tr {
    border-bottom: 1px solid #dddddd;
  }

  tbody tr:nth-of-type(even) {
    background-color: #f3f3f3;
  }

  tbody tr:last-of-type {
    border-bottom: 2px solid #009879;
  }

  tbody tr.active-row {
    font-weight: bold;
    color: #009879;
  }

  @media print {
    box-shadow: none;
    width: 100%;
    
    thead tr {
      background-color: #009879 !important;
      color: #ffffff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    th, td {
      border: 1px solid #ddd;
    }
  }
`;

const Footer = styled.div`
  margin-top: 4rem;
  text-align: center;
  
  @media print {
    margin-top: 2rem;
  }
`;

const HODStamp = styled.div`
  margin-bottom: 1rem;
  font-weight: bold;
  text-decoration: underline;
`;

const Disclaimer = styled.i`
  font-size: 0.8rem;
  color: #666;
`;

const PrintHeaderWrapper = styled.div`
  display: none;
  @media print {
    display: block;
    margin-bottom: 2rem;
  }
`;

const ModuleForm = () => {
  const { user } = useAuthStore();
  const [data, setdata] = React.useState(undefined);
  const [infos, setinfos] = React.useState(undefined);
  const [loading, setloading] = React.useState(true);
  const [programme, setprogramme] = React.useState(undefined);
  const [levs, setlevs] = React.useState(undefined);
  const [trimester, settrimester] = React.useState(undefined);

  React.useEffect(() => {
    (async () => {
      try {
        const { _id: id } = user;
        const req = await axiosget(`ms_course_result/student/${id}`);
        if (req.data?.type) {
          throw new Error(req.data?.message);
        }
        const { info, prog, levs: lev, tri } = req.data;
        settrimester(tri);
        setinfos(info);
        setlevs(lev);
        setprogramme(prog);
        const reqm = await axiosget("ms_course/student");
        if (reqm.data?.type) {
          throw new Error(req.data?.message);
        }
        setdata(reqm.data);
      } catch ({ message }) {
        console.warn(message);
      }
      setloading(false);
    })();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      {loading ? (
        <Loader message={`Please wait while we load your result`} img="../images/loading.gif" />
      ) : (
        <>
          {!is_null(data) && data.length > 0 && (
            <>
              <PrintHeaderWrapper>
                <LetterHeader />
              </PrintHeaderWrapper>

              <StudentInfoCard>
                <InfoItem>
                  <Label>Student Name</Label>
                  <Value>{infos.firstname} {infos.middlename} {infos.lastname}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Student ID</Label>
                  <Value>{infos.studentid}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Programme</Label>
                  <Value>{programme?.name}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Level & Semester</Label>
                  <Value>{levs.level} {levs.semester}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Session</Label>
                  <Value>{trimester?.code} | {trimester?.name}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Printed Date</Label>
                  <Value>{new Date().toISOString()}</Value>
                </InfoItem>
              </StudentInfoCard>

              <TableContainer>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Course Code</th>
                      <th>Course Title</th>
                      <th>Credit Unit</th>
                      <th>Status</th>
                      <th>Level</th>
                      <th>Semester</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filteredData = data?.filter((v) => v.session?._id === trimester?._id) || [];
                      const totalCredit = filteredData.reduce((sum, v) => sum + (v.courseinfo?.credit || 0), 0);

                      return (
                        <>
                          {filteredData.map((v, i) => (
                            <tr key={v._id || i}>
                              <td>{i + 1}</td>
                              <td>{v.courseinfo?.code}</td>
                              <td>{v.courseinfo?.name}</td>
                              <td>{v.courseinfo?.credit}</td>
                              <td>{v.isfresh ? "Fresh" : "Carryover"}</td>
                              <td>{v.level}</td>
                              <td>{v.semester}</td>
                            </tr>
                          ))}
                          {filteredData.length > 0 && (
                            <tr style={{ backgroundColor: "#e6fffa", borderTop: "2px solid #009879" }}>
                              <td colSpan="3" style={{ textAlign: "right", fontWeight: "bold", textTransform: "uppercase" }}>
                                Total Credit Unit:
                              </td>
                              <td style={{ fontWeight: "bold", color: "#009879" }}>{totalCredit}</td>
                              <td colSpan="3"></td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </StyledTable>
              </TableContainer>

              <Footer>
                <HODStamp>HOD STAMP & DATE</HODStamp>
                <Disclaimer>
                  Tempering with any score/grade on this document makes it invalid, students are advised to leave this statement of result as is.
                </Disclaimer>
              </Footer>
            </>
          )}

          {!is_null(data) && data.length <= 0 && (
            <div className="alert alert-info alert-dismissible fade show mt-5" role="alert">
              <strong>Sorry!</strong> Results are not out for the selected semester
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default ModuleForm;
